import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  MessageCircle,
  MapPin,
  Clock,
  Phone,
  ShieldAlert,
  Zap,
  Droplet,
  Eye,
  ArrowRight,
  Info,
} from "lucide-react";
import { CLINIC, waLink, telLink } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import {
  SITE,
  breadcrumb,
  graph,
  CLINIC_NODE,
  AUTHOR_REF,
  PUBLISHER_REF,
  REF,
} from "../lib/schema";
import SectionHeader from "../components/site/SectionHeader";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";

// ---------------------------------------------------------------------------
// Dedicated Emergency Eye Care landing page.
// Targets high-commercial-intent queries — "emergency eye doctor near me",
// "eye emergency Varanasi", "chemical injury eye first aid", "sudden vision
// loss Varanasi".  WhatsApp-first CTA (user preference).  Rich EmergencyService
// + FAQPage schema for AI Overviews + Google featured snippets.
// ---------------------------------------------------------------------------

const CANONICAL_PATH = "/emergency-eye-care-varanasi";

const EMERGENCIES = [
  {
    icon: Droplet,
    title: "Chemical splash / lime / acid in the eye",
    urgency: "GOLDEN 15 MINUTES",
    firstAid:
      "Immediately flush the eye with copious clean water or saline for a full 15-20 minutes before travelling. Do not stop early. Do not try to neutralise (e.g. water on acid, or vice versa). Then message us on WhatsApp with a photo of the eye — we'll coordinate immediate arrival.",
    danger:
      "Alkali (lime/whitewash/chuna) is worse than acid — it keeps penetrating. Every minute of delay damages more corneal stem cells.",
  },
  {
    icon: Zap,
    title: "Sudden painless vision loss",
    urgency: "WITHIN 4 HOURS",
    firstAid:
      "Sudden, painless loss of vision in one eye can be a central retinal artery occlusion (a stroke of the eye). Do not lie down. Come immediately. If we're closed, go to the nearest emergency department while we coordinate.",
    danger:
      "Retinal artery occlusion has a 4-hour window before permanent damage. Diabetic retinopathy bleed and retinal detachment also present this way.",
  },
  {
    icon: AlertTriangle,
    title: "Sudden flashes + floaters + curtain over vision",
    urgency: "SAME-DAY",
    firstAid:
      "A shower of new floaters, flashes of light, or a dark curtain / shadow moving across your visual field is a retinal detachment warning. Do not rub the eye. Avoid heavy lifting or sudden head movements. Come the same day for a dilated exam.",
    danger:
      "Left untreated, a peripheral retinal tear progresses to detachment within days — and detachment past the macula causes permanent central vision loss.",
  },
  {
    icon: Eye,
    title: "Foreign body / metal / glass in the eye",
    urgency: "SAME-DAY",
    firstAid:
      "Do NOT rub the eye. Do NOT try to remove embedded objects yourself. Cover the eye lightly with a clean cloth (do not press). If both eyes are affected, cover both. Come immediately — embedded metal starts rusting into the cornea within hours.",
    danger:
      "Grinder / welding / hammer-and-chisel injuries frequently embed micro-fragments that patients think are 'gone' but which are lodged inside the cornea. Missed on GP exam.",
  },
  {
    icon: ShieldAlert,
    title: "Severe eye pain + redness + blurred vision + halos",
    urgency: "WITHIN 6 HOURS",
    firstAid:
      "Severe pain with headache, nausea, blurred vision and coloured halos around lights can be an acute angle-closure glaucoma attack. This is a true emergency — the eye pressure needs to be lowered within hours to save vision.",
    danger:
      "Common in patients above 45, more so in women, and often triggered by dim-light activities. Untreated acute glaucoma causes permanent optic nerve damage within 24-48 hours.",
  },
  {
    icon: AlertTriangle,
    title: "Blunt trauma / eye punch / cricket ball / airbag hit",
    urgency: "SAME-DAY",
    firstAid:
      "Do NOT rub or press the eye. If vision is affected, if there is bleeding inside the eye (visible red pool at the bottom of the coloured part), or if you saw flashes / floaters afterwards — come immediately. Cover the eye with a shield / plastic cup taped over, not a bandage.",
    danger:
      "Hyphaema (blood inside the eye), traumatic cataract and retinal tears can all follow a hit that seems 'mild' initially.",
  },
];

const DO_NOT = [
  "Do NOT rub the eye — you can drive a foreign body deeper or dislodge a retinal patch.",
  "Do NOT apply home remedies (honey, milk, turmeric, urine, kajal, castor oil) — these delay real treatment and cause secondary infections.",
  "Do NOT put pressure on a hit eye — this can rupture an already-weakened globe.",
  "Do NOT wait for the eye to 'settle' overnight. Most sight-threatening emergencies have a golden window measured in hours.",
  "Do NOT self-medicate with steroid eye drops (Predsol, Eco, Lo-Pred etc.) — steroids on top of a hidden infection or ulcer can cause blindness.",
];

const EMERGENCY_FAQS = [
  {
    q: "Is there an emergency eye doctor available in Varanasi at night?",
    a: "Dr. Shantanu Kumar Gupta and Dr. Manisha Mishra coordinate on-call emergency eye care for Shantanu Netralaya patients. During non-clinic hours, please message us on WhatsApp with a description of the emergency and a clear photograph of the affected eye — we will triage the case and either arrange after-hours arrival at the clinic (for sight-threatening cases) or advise on immediate first-aid + earliest morning appointment.",
  },
  {
    q: "What counts as an eye emergency that cannot wait until morning?",
    a: "Sight-threatening emergencies include: chemical splashes (lime, acid, chuna), sudden painless loss of vision in one eye, a sudden shower of new floaters or flashes with a shadow moving across vision (retinal detachment warning), severe eye pain with headache and haloes around lights (possible acute angle-closure glaucoma), high-velocity foreign body injuries (grinder, welding, hammer chips), and blunt eye trauma with bleeding inside the coloured part of the eye (hyphaema).",
  },
  {
    q: "What is the first aid for a chemical splash in the eye before reaching the hospital?",
    a: "Immediately flush the eye with copious clean water or saline for a full 15-20 minutes without stopping. This is the single most important step and it must be done before travel — even a 5 minute delay while you drive to hospital worsens the injury. Do not try to neutralise (e.g. water on acid, or lemon on lime). After flushing, come immediately for pH testing and further treatment.",
  },
  {
    q: "Can I drive myself to hospital during an eye emergency?",
    a: "No — for any sudden vision loss, severe pain, or trauma, please arrange someone else to drive. Even 'good enough' vision in one eye can hide serious depth-perception and side-vision loss that makes driving unsafe. If you're alone, call an auto / cab / ambulance.",
  },
  {
    q: "How do I share an eye emergency with the doctor over WhatsApp?",
    a: "Send us a message on WhatsApp (+91 79058 54244). Include: (1) a clear front-facing photo of the affected eye in good light, (2) what happened and when, (3) whether vision is affected in one or both eyes, and (4) any first aid already done. We reply as fast as possible during clinic hours and coordinate after-hours arrival for sight-threatening cases.",
  },
];

const EmergencyEyeCare = () => {
  usePageMeta({
    title:
      "Emergency Eye Care in Varanasi — 24×7 WhatsApp Triage | Shantanu Netralaya",
    path: CANONICAL_PATH,
    description:
      "Eye emergency in Varanasi? Chemical splash, sudden vision loss, retinal detachment warning signs, foreign body injury, blunt trauma or severe eye pain — message Dr. Shantanu Kumar Gupta on WhatsApp for immediate triage. Includes first-aid steps and after-hours coordination at Shantanu Netralaya.",
  });

  const jsonLd = useMemo(
    () =>
      graph([
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Emergency Eye Care", path: CANONICAL_PATH },
        ]),
        CLINIC_NODE,
        {
          "@type": "EmergencyService",
          "@id": `${SITE}${CANONICAL_PATH}#emergency`,
          name: "Emergency Eye Care — Shantanu Netralaya, Varanasi",
          url: `${SITE}${CANONICAL_PATH}`,
          telephone: CLINIC.phone,
          image: `${SITE}/logo.png`,
          areaServed: [
            { "@type": "City", name: "Varanasi" },
            { "@type": "AdministrativeArea", name: "Purvanchal, Uttar Pradesh" },
            { "@type": "AdministrativeArea", name: "Kaimur & Rohtas, Bihar" },
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: CLINIC.address,
            addressLocality: "Varanasi",
            addressRegion: "Uttar Pradesh",
            postalCode: "221005",
            addressCountry: "IN",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: CLINIC.geo.lat,
            longitude: CLINIC.geo.lng,
          },
          provider: { "@id": REF.clinic },
        },
        {
          "@type": "MedicalWebPage",
          "@id": `${SITE}${CANONICAL_PATH}#page`,
          url: `${SITE}${CANONICAL_PATH}`,
          name: "Emergency Eye Care in Varanasi",
          inLanguage: "en-IN",
          author: AUTHOR_REF,
          reviewedBy: AUTHOR_REF,
          publisher: PUBLISHER_REF,
          specialty: { "@type": "MedicalSpecialty", name: "Ophthalmology" },
          audience: {
            "@type": "PatientExperience",
            healthCondition:
              "Eye emergency, chemical injury, retinal detachment, acute angle-closure glaucoma, ocular trauma",
          },
        },
        {
          "@type": "FAQPage",
          "@id": `${SITE}${CANONICAL_PATH}#faq`,
          mainEntity: EMERGENCY_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ]),
    []
  );
  usePageJsonLd(jsonLd);

  const emergencyWa = waLink(
    "🚨 EYE EMERGENCY — I need immediate help. Here's what happened: [describe injury / symptom]. Photo of eye attached."
  );

  return (
    <div data-testid="emergency-eye-care-page">
      {/* Emergency banner */}
      <div className="bg-red-600 text-white" data-testid="emergency-banner">
        <div className="container-page py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-[13px] font-medium">
            <AlertTriangle size={16} /> Eye emergency? Message us on WhatsApp
            first — every minute matters.
          </div>
          <a
            href={emergencyWa}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] bg-white text-red-700 px-4 py-2 rounded-full hover:bg-red-50 transition font-medium"
            data-testid="banner-whatsapp-cta"
          >
            <MessageCircle size={13} /> Chat now
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-14 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2 text-red-700">
          <ShieldAlert size={14} strokeWidth={1.5} /> 24×7 WhatsApp triage
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Eye Emergency in Varanasi? Get help on WhatsApp — fast.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
          A chemical splash, sudden loss of vision, flashes and floaters after a
          fall, or a fragment stuck in the eye — every one of these has a
          golden window measured in minutes to hours. Message us on WhatsApp
          with a photograph of the eye and Dr. Shantanu Kumar Gupta will triage
          your case immediately.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a
            href={emergencyWa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="btn-primary bg-red-600 hover:bg-red-700 border-red-600 text-white inline-flex items-center gap-2 text-sm"
          >
            <MessageCircle size={16} /> Start emergency WhatsApp
          </a>
          <a
            href={telLink()}
            className="btn-outline text-xs inline-flex items-center gap-2"
            data-testid="hero-call-cta"
          >
            <Phone size={13} /> Call {CLINIC.phone}
          </a>
        </div>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl">
          {[
            { icon: Clock, k: "First reply", v: "within minutes (day) / 30 min (night)" },
            { icon: MapPin, k: "Location", v: "Durgakund, Varanasi" },
            { icon: MessageCircle, k: "WhatsApp", v: CLINIC.whatsapp },
          ].map(({ icon: Icon, k, v }) => (
            <div
              key={k}
              className="p-4 rounded-xl border border-border bg-background/60"
              data-testid="emergency-stat-card"
            >
              <Icon size={16} className="text-red-600" />
              <div className="mt-2 text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">
                {k}
              </div>
              <div className="mt-1 text-sm font-medium leading-tight">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Emergency categories */}
      <section
        className="py-12 border-t border-border container-page"
        data-testid="emergency-categories"
      >
        <SectionHeader
          overline="Recognise the emergency"
          title="Sight-threatening emergencies — what to do in the first minutes"
        />
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          The first few minutes are the most important. Do the right first-aid
          step at home <em>while you WhatsApp us the details</em> — it changes
          the outcome more than anything the doctor does later.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {EMERGENCIES.map((e) => (
            <article
              key={e.title}
              className="rounded-2xl border border-border p-6 bg-background hover:border-red-600/40 transition-colors"
              data-testid="emergency-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-red-600/10 text-red-600">
                    <e.icon size={20} strokeWidth={1.75} />
                  </div>
                  <div className="font-serif text-lg leading-tight">
                    {e.title}
                  </div>
                </div>
                <span className="text-[9.5px] uppercase tracking-[0.2em] bg-red-600/10 text-red-700 px-2 py-1 rounded-full shrink-0">
                  {e.urgency}
                </span>
              </div>
              <div className="mt-4 text-sm leading-relaxed">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mb-1.5">
                  First aid at home
                </div>
                <p>{e.firstAid}</p>
              </div>
              <div className="mt-4 pt-4 border-t border-border text-[12.5px] text-muted-foreground leading-relaxed flex gap-2">
                <Info
                  size={13}
                  className="text-red-600 mt-0.5 shrink-0"
                  strokeWidth={2}
                />
                <span>{e.danger}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Do Nots */}
      <section
        className="py-12 border-t border-border container-page"
        data-testid="emergency-donts"
      >
        <SectionHeader
          overline="Critical don'ts"
          title="Never do these five things in an eye emergency"
        />
        <ul className="mt-6 grid gap-3 md:grid-cols-2">
          {DO_NOT.map((d) => (
            <li
              key={d}
              className="flex items-start gap-3 p-4 rounded-xl border border-red-600/30 bg-red-600/5"
            >
              <ShieldAlert
                size={16}
                className="text-red-600 mt-0.5 shrink-0"
                strokeWidth={2}
              />
              <span className="text-sm leading-relaxed">{d}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* WhatsApp instruction block */}
      <section
        className="py-14 border-t border-border container-page"
        data-testid="emergency-whatsapp-guide"
      >
        <div className="rounded-3xl border border-green-600/25 bg-green-600/5 p-8 md:p-12 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="overline mb-3 inline-flex items-center gap-2 text-green-700">
              <MessageCircle size={13} /> Fastest path to help
            </div>
            <h2 className="font-serif text-3xl leading-tight">
              Send us these 4 things on WhatsApp
            </h2>
            <ol className="mt-5 space-y-2 text-sm leading-relaxed list-decimal list-inside marker:text-green-700 marker:font-semibold">
              <li>A clear front-facing photograph of the affected eye in good light</li>
              <li>What happened, and when (time of injury / onset of symptom)</li>
              <li>Whether one eye or both are affected + your current vision</li>
              <li>Any first-aid already done and any allergies to medicines</li>
            </ol>
            <p className="text-[11.5px] text-muted-foreground mt-4">
              Dr. Shantanu Kumar Gupta triages the message personally. For
              sight-threatening cases, we coordinate after-hours arrival at the
              clinic in Durgakund immediately.
            </p>
          </div>
          <a
            href={emergencyWa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="guide-whatsapp-cta"
            className="btn-primary bg-green-600 hover:bg-green-700 border-green-600 text-white inline-flex items-center gap-2 shrink-0"
          >
            <MessageCircle size={16} /> WhatsApp now
            <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Clinic locator */}
      <section
        className="py-14 border-t border-border container-page"
        data-testid="emergency-clinic"
      >
        <SectionHeader
          overline="How to reach us"
          title="Shantanu Netralaya — Durgakund, Varanasi"
        />
        <div className="mt-8 grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-border overflow-hidden aspect-video bg-[hsl(var(--secondary))]/30">
            <iframe
              src={CLINIC.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Shantanu Netralaya location"
            ></iframe>
          </div>
          <div className="text-sm space-y-4 leading-relaxed">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">
                Address
              </div>
              <div className="mt-1 font-medium">{CLINIC.address}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">
                Clinic hours
              </div>
              <div className="mt-1 space-y-1">
                {CLINIC.timings.map((t) => (
                  <div key={t.day}>
                    <span className="font-medium">{t.day}:</span> {t.hours}
                  </div>
                ))}
              </div>
              <div className="mt-1 text-[11.5px] text-red-700">
                Outside these hours — WhatsApp triage first.
              </div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">
                Plus code
              </div>
              <div className="mt-1 font-mono text-sm">{CLINIC.plusCode}</div>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href={CLINIC.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline text-xs inline-flex items-center gap-2"
              >
                <MapPin size={13} /> Open in Google Maps
              </a>
              <a
                href={telLink()}
                className="btn-outline text-xs inline-flex items-center gap-2"
              >
                <Phone size={13} /> Call clinic
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-14 border-t border-border container-page"
        data-testid="emergency-faq"
      >
        <SectionHeader
          overline="FAQs"
          title="Emergency eye care — frequently asked questions"
        />
        <div className="mt-6 divide-y divide-border rounded-2xl border border-border overflow-hidden">
          {EMERGENCY_FAQS.map((f) => (
            <details
              key={f.q}
              className="group open:bg-[hsl(var(--secondary))]/20"
              data-testid="emergency-faq-item"
            >
              <summary className="cursor-pointer list-none p-5 flex items-start gap-4 hover:bg-[hsl(var(--secondary))]/20">
                <Info
                  size={14}
                  className="mt-1 text-red-600 shrink-0"
                />
                <div className="flex-1 font-medium text-[15px] pr-6">{f.q}</div>
                <span className="text-red-600 text-lg leading-none select-none group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <div className="px-5 pb-5 pl-14 text-sm text-muted-foreground leading-relaxed">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-16 border-t border-border container-page text-center"
        data-testid="emergency-bottom-cta"
      >
        <div className="rounded-3xl bg-red-600/8 border border-red-600/25 p-10 md:p-14 max-w-3xl mx-auto">
          <div className="overline mb-3 inline-flex items-center gap-2 text-red-700">
            <ShieldAlert size={13} /> Don't wait
          </div>
          <h2 className="font-serif text-3xl leading-tight">
            When in doubt, WhatsApp first.
          </h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl mx-auto">
            It costs nothing to send a photograph and get a fast triage decision
            — and it can save your vision.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href={emergencyWa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bottom-whatsapp-cta"
              className="btn-primary bg-red-600 hover:bg-red-700 border-red-600 text-white inline-flex items-center gap-2"
            >
              <MessageCircle size={16} /> Emergency WhatsApp
            </a>
            <Link
              to="/symptom-checker"
              className="btn-outline text-xs inline-flex items-center gap-2"
            >
              Symptom checker (non-emergency)
            </Link>
          </div>
        </div>
        <div className="mt-8 max-w-2xl mx-auto">
          <MedicalDisclaimer />
        </div>
      </section>
    </div>
  );
};

export default EmergencyEyeCare;
